/**
 * AP-49 — **Projektbesprechung** (Baustein `SITE_DOCS.MEETING`): was Gerät, ERP und Server über die
 * Art gleich wissen müssen — Teilnehmer, Tagesordnung, Punkte, Serie und Laufnummer, Fortschreibung
 * und vor allem der **kanonische Fachteil** (`besprechungFachteil`), den alle Seiten mit derselben
 * Funktion bauen (AP-46 6.).
 *
 * Spezifikation: docs/Arbeitspakete/AP-49/AP-49_Projektbesprechung.md
 */
import { kanonischerText } from './kanonisch'

/** Fassung des Fachteils. Eine Änderung am Aufbau unten ist eine neue Fassung (AP-46 6.). */
export const BESPRECHUNG_SCHEMA_VERSION = 1

export const BESPRECHUNG_FORMEN = ['PROTOKOLL', 'BERICHTIGUNG'] as const
export type BesprechungForm = (typeof BESPRECHUNG_FORMEN)[number]

/** Anwesenheit — `null` heißt „nur im Verteiler" (5.1). Werte wie das Prisma-Enum `SiteMeetingAttendance`. */
export const BESPRECHUNG_ANWESENHEITEN = ['PRESENT', 'EXCUSED', 'ABSENT'] as const
export type BesprechungAnwesenheit = (typeof BESPRECHUNG_ANWESENHEITEN)[number]

/** Punktart (BS-4) — wie das Prisma-Enum `SiteMeetingItemKind`. */
export const BESPRECHUNG_PUNKTARTEN = ['INFO', 'DECISION', 'TASK'] as const
export type BesprechungPunktart = (typeof BESPRECHUNG_PUNKTARTEN)[number]

/** Stand einer Aufgabe — wie das Prisma-Enum `SiteMeetingItemState`. */
export const BESPRECHUNG_STAENDE = ['OPEN', 'DONE', 'DROPPED'] as const
export type BesprechungStand = (typeof BESPRECHUNG_STAENDE)[number]

/** Kürzel der Punktart in Tastatur und PDF (11.1, 11.3): I, B, A. */
export const BESPRECHUNG_KUERZEL: Record<BesprechungPunktart, string> = { INFO: 'I', DECISION: 'B', TASK: 'A' }

const FACH_ANWESENHEIT = { PRESENT: 'ANWESEND', EXCUSED: 'ENTSCHULDIGT', ABSENT: 'ABWESEND' } as const
const FACH_ART = { INFO: 'INFO', DECISION: 'BESCHLUSS', TASK: 'AUFGABE' } as const
const FACH_STATUS = { OPEN: 'OFFEN', DONE: 'ERLEDIGT', DROPPED: 'ENTFAELLT' } as const

// ── Einstellungen der Art (BS-2, BS-10) ──────────────────────────────────────

/** Einstellungen der Art in `SiteDocumentSetting.extra`. */
export interface BesprechungExtra {
  /** Besprechungsarten des Mandanten — jede bildet mit dem Projekt eine Serie. */
  arten: string[]
  /** Einspruchsfrist in Tagen; 0 = keine Frist im Protokoll. */
  einspruchTage: number
  /** Text zur Frist — `{Tage}` wird ersetzt. Was Schweigen bedeutet, sagt der Mandant (16.3). */
  einspruchText: string
}

export const BESPRECHUNG_ARTEN_VORGABE = ['Baubesprechung', 'Jour fixe', 'Planungsbesprechung', 'Interne Besprechung'] as const

export const BESPRECHUNG_EINSPRUCH_TEXT_VORGABE =
  'Einwände gegen dieses Protokoll bitte innerhalb von {Tage} Tagen nach Erhalt schriftlich an den Verfasser.'

export const BESPRECHUNG_EXTRA_VORGABE: BesprechungExtra = {
  arten: [...BESPRECHUNG_ARTEN_VORGABE],
  einspruchTage: 7,
  einspruchText: BESPRECHUNG_EINSPRUCH_TEXT_VORGABE,
}

export function besprechungExtra(extra: unknown): BesprechungExtra {
  const e = (extra ?? {}) as Partial<BesprechungExtra>
  const arten = Array.isArray(e.arten) ? [...new Set(e.arten.filter((a): a is string => typeof a === 'string').map((a) => a.trim()).filter(Boolean))] : []
  const tage = Number(e.einspruchTage)
  return {
    arten: arten.length ? arten : [...BESPRECHUNG_ARTEN_VORGABE],
    einspruchTage: Number.isInteger(tage) && tage >= 0 && tage <= 365 ? tage : BESPRECHUNG_EXTRA_VORGABE.einspruchTage,
    einspruchText: typeof e.einspruchText === 'string' && e.einspruchText.trim() ? e.einspruchText.trim() : BESPRECHUNG_EINSPRUCH_TEXT_VORGABE,
  }
}

/** Die Einspruchsfrist, wie sie beim Anlegen ins Protokoll geht — mit eingesetzten Tagen. */
export function besprechungEinspruch(extra: BesprechungExtra): BesprechungEinspruch | null {
  if (!extra.einspruchTage) return null
  return { tage: extra.einspruchTage, text: extra.einspruchText.split('{Tage}').join(String(extra.einspruchTage)) }
}

// ── Inhalt, wie Gerät und ERP ihn führen ─────────────────────────────────────

/** Teilnehmer und Verteiler (5.1). E-Mail und Zuordnungen sind Büroarbeit, kein Inhalt (BS-8). */
export interface BesprechungTeilnehmer {
  clientId: string
  name: string
  company: string | null
  function: string | null
  attendance: BesprechungAnwesenheit | null
  distribution: boolean
  sortOrder: number
  // Zuordnung — nicht im Fachteil
  email?: string | null
  userId?: string | null
  contactId?: string | null
  // Nur in Antworten des Servers
  id?: string
  sentAt?: string | null
}

/** Ein Tagesordnungspunkt — Reihenfolge = Reihenfolge der Liste. */
export interface BesprechungTop {
  key: string
  titel: string
}

/** Ein Punkt der Besprechung (5.2). */
export interface BesprechungPunkt {
  clientId: string
  /** Kennung der ersten Erfassung — bei neuen Punkten leer bzw. die eigene; verbindet einen Punkt über die Serie. */
  originId?: string | null
  /** „7.4" für neue, „5.3" für übernommene. */
  number: string
  /** Schlüssel des Tagesordnungspunkts oder leer. */
  agendaKey: string | null
  kind: BesprechungPunktart
  text: string
  assigneeName?: string | null
  assigneeCompany?: string | null
  /** Zuordnung — ein Mitarbeiter des Betriebs; steuert die Aufgabe im Projekt (BS-5). */
  assigneeUserId?: string | null
  /** Termin als Datum `JJJJ-MM-TT` … */
  dueDate?: string | null
  /** … oder als Text („laufend", „vor Montagebeginn"). */
  dueText?: string | null
  state?: BesprechungStand | null
  /** Herkunftsvermerk übernommener Punkte („aus Nr. 5"). */
  carriedFrom?: string | null
  sortOrder: number
  // Nur in Antworten des Servers
  id?: string
}

/** Ein Einwand in einer Berichtigung (7.2). */
export interface BesprechungEinwand {
  key: string
  /** Name, Firma — wie der Einwand kam. */
  von: string
  /** Eingang am `JJJJ-MM-TT`. */
  eingangAm: string
  /** Nummer des betroffenen Punkts („6.2"), optional. */
  punkt: string | null
  einwand: string
  /** Berichtigung — optional: ein Einwand kann auch nur festgehalten werden. */
  berichtigung: string | null
}

export interface BesprechungNaechste {
  datum: string
  beginn: string | null
  ort: string | null
}

export interface BesprechungEinspruch {
  tage: number
  text: string
}

/** Der Kopf-`content`: alles außer Teilnehmern und Punkten (die führt die Art in eigenen Tabellen, BS-13). */
export interface BesprechungKopf {
  form: BesprechungForm
  art: string
  laufnummer: number
  beginn: string | null
  ende: string | null
  ort: string | null
  leitung: string | null
  tagesordnung: BesprechungTop[]
  naechste: BesprechungNaechste | null
  einspruch: BesprechungEinspruch | null
  bemerkung: string | null
  /** Nur bei `BERICHTIGUNG`. */
  berichtigungen: BesprechungEinwand[]
}

/** Der Fachinhalt, wie er übertragen wird: Kopf samt Teilnehmern und Punkten. */
export interface BesprechungInhalt extends BesprechungKopf {
  teilnehmer: BesprechungTeilnehmer[]
  punkte: BesprechungPunkt[]
}

export function besprechungLeererKopf(art = '', laufnummer = 1): BesprechungKopf {
  return { form: 'PROTOKOLL', art, laufnummer, beginn: null, ende: null, ort: null, leitung: null, tagesordnung: [], naechste: null, einspruch: null, bemerkung: null, berichtigungen: [] }
}

// ── Reihenfolge ──────────────────────────────────────────────────────────────

/** „5.3" < „5.10" < „7.1" — Teile als Zahlen, sonst als Text. */
export function besprechungNummernVergleich(a: string, b: string): number {
  const ta = a.split('.')
  const tb = b.split('.')
  for (let i = 0; i < Math.max(ta.length, tb.length); i++) {
    const x = ta[i] ?? ''
    const y = tb[i] ?? ''
    const nx = Number(x)
    const ny = Number(y)
    const d = x !== '' && y !== '' && Number.isFinite(nx) && Number.isFinite(ny) ? nx - ny : x < y ? -1 : x > y ? 1 : 0
    if (d) return d
  }
  return 0
}

/**
 * Fachliche Reihenfolge der Punkte (6.1): nach Tagesordnung, Punkte ohne Tagesordnungspunkt am
 * Ende, dann Nummer, bei Gleichstand die Kennung — auf jedem Gerät gleich.
 */
export function besprechungPunkteSortiert<T extends Pick<BesprechungPunkt, 'agendaKey' | 'number' | 'clientId'>>(punkte: readonly T[], tagesordnung: readonly BesprechungTop[]): T[] {
  const rang = new Map(tagesordnung.map((t, i) => [t.key, i]))
  const r = (p: T) => (p.agendaKey != null && rang.has(p.agendaKey) ? rang.get(p.agendaKey)! : tagesordnung.length)
  return [...punkte].sort((a, b) => r(a) - r(b) || besprechungNummernVergleich(a.number, b.number) || (a.clientId < b.clientId ? -1 : a.clientId > b.clientId ? 1 : 0))
}

/** Teilnehmer wie erfasst: `sortOrder`, bei Gleichstand die Kennung. */
export function besprechungTeilnehmerSortiert<T extends Pick<BesprechungTeilnehmer, 'sortOrder' | 'clientId'>>(liste: readonly T[]): T[] {
  return [...liste].sort((a, b) => a.sortOrder - b.sortOrder || (a.clientId < b.clientId ? -1 : a.clientId > b.clientId ? 1 : 0))
}

// ── Kanonischer Fachteil (6.1) ───────────────────────────────────────────────

const t = (w: string | null | undefined): string | null => {
  const k = w == null ? '' : kanonischerText(w)
  return k ? k : null
}

/**
 * **Der kanonische Fachteil**, `schemaVersion` 1 (AP-49 6.1). E-Mail-Adressen, `userId`, Kontakt- und
 * Herkunfts-IDs stehen bewusst **nicht** darin (BS-8, AP-46 BD-24). Leere Angaben sind `null`, nicht
 * weggelassen — jede Seite schreibt denselben Schlüsselsatz.
 */
export function besprechungFachteil(inhalt: BesprechungInhalt): Record<string, unknown> {
  const teilnehmer = besprechungTeilnehmerSortiert(inhalt.teilnehmer).map((p) => ({
    name: kanonischerText(p.name ?? ''),
    firma: t(p.company),
    funktion: t(p.function),
    anwesenheit: p.attendance ? FACH_ANWESENHEIT[p.attendance] : null,
    verteiler: !!p.distribution,
  }))
  if (inhalt.form === 'BERICHTIGUNG') {
    return {
      form: 'BERICHTIGUNG',
      art: kanonischerText(inhalt.art ?? ''),
      laufnummer: inhalt.laufnummer,
      teilnehmer,
      berichtigungen: (inhalt.berichtigungen ?? []).map((e) => ({
        von: kanonischerText(e.von ?? ''),
        eingangAm: (e.eingangAm ?? '').slice(0, 10),
        punkt: t(e.punkt),
        einwand: kanonischerText(e.einwand ?? ''),
        berichtigung: t(e.berichtigung),
      })),
      bemerkung: t(inhalt.bemerkung),
    }
  }
  const tagesordnung = (inhalt.tagesordnung ?? []).map((x) => ({ key: kanonischerText(x.key), titel: kanonischerText(x.titel ?? '') }))
  return {
    form: 'PROTOKOLL',
    art: kanonischerText(inhalt.art ?? ''),
    laufnummer: inhalt.laufnummer,
    beginn: t(inhalt.beginn),
    ende: t(inhalt.ende),
    ort: t(inhalt.ort),
    leitung: t(inhalt.leitung),
    teilnehmer,
    tagesordnung,
    punkte: besprechungPunkteSortiert(inhalt.punkte, inhalt.tagesordnung ?? []).map((p) => {
      const aufgabe = p.kind === 'TASK'
      const name = aufgabe ? t(p.assigneeName) : null
      const termin = aufgabe && (t(p.dueDate) || t(p.dueText)) ? { datum: t(p.dueDate)?.slice(0, 10) ?? null, text: t(p.dueText) } : null
      return {
        key: kanonischerText(p.clientId),
        nummer: kanonischerText(p.number ?? ''),
        top: p.agendaKey && tagesordnung.some((x) => x.key === p.agendaKey) ? p.agendaKey : null,
        art: FACH_ART[p.kind],
        text: kanonischerText(p.text ?? ''),
        zustaendig: name ? { name, firma: t(p.assigneeCompany) } : null,
        termin,
        status: aufgabe && p.state ? FACH_STATUS[p.state] : null,
        herkunft: t(p.carriedFrom),
      }
    }),
    naechste: inhalt.naechste?.datum ? { datum: inhalt.naechste.datum.slice(0, 10), beginn: t(inhalt.naechste.beginn), ort: t(inhalt.naechste.ort) } : null,
    einspruch: inhalt.einspruch ? { tage: inhalt.einspruch.tage, text: kanonischerText(inhalt.einspruch.text ?? '') } : null,
    bemerkung: t(inhalt.bemerkung),
  }
}

// ── Pflichtangaben (6.2) ─────────────────────────────────────────────────────

const ZEIT = /^(\d{1,2}):(\d{2})$/

/** `HH:mm` → Minuten; leer oder unlesbar → `null`. */
export function besprechungMinuten(zeit: string | null | undefined): number | null {
  const m = ZEIT.exec((zeit ?? '').trim())
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  return h > 23 || min > 59 ? null : h * 60 + min
}

/**
 * Pflicht vor dem Abschluss (6.2). Protokoll: Art, Laufnummer ≥ 1, mindestens ein Anwesender, mindestens
 * ein Punkt, jeder Punkt mit Text, jede Aufgabe mit Status, `ende` nicht vor `beginn`. Berichtigung:
 * mindestens ein Einwand mit von, Eingang und Text.
 */
export function besprechungPruefe(inhalt: BesprechungInhalt): string[] {
  const fehler: string[] = []
  if (inhalt.form === 'BERICHTIGUNG') {
    if (!inhalt.berichtigungen?.length) fehler.push('objections')
    for (const e of inhalt.berichtigungen ?? []) {
      if (!kanonischerText(e.von ?? '')) fehler.push(`objection:${e.key}:objectionFrom`)
      if (!/^\d{4}-\d{2}-\d{2}/.test(e.eingangAm ?? '')) fehler.push(`objection:${e.key}:objectionReceived`)
      if (!kanonischerText(e.einwand ?? '')) fehler.push(`objection:${e.key}:objectionText`)
    }
    return fehler
  }
  if (!kanonischerText(inhalt.art ?? '')) fehler.push('meetingType')
  if (!Number.isInteger(inhalt.laufnummer) || inhalt.laufnummer < 1) fehler.push('sequence')
  if (!inhalt.teilnehmer.some((p) => p.attendance === 'PRESENT')) fehler.push('present')
  if (!inhalt.punkte.length) fehler.push('items')
  for (const p of inhalt.punkte) {
    if (!kanonischerText(p.text ?? '')) fehler.push(`item:${p.clientId}:itemText`)
    if (p.kind === 'TASK' && !p.state) fehler.push(`item:${p.clientId}:taskState`)
  }
  const a = besprechungMinuten(inhalt.beginn)
  const b = besprechungMinuten(inhalt.ende)
  if (a !== null && b !== null && b < a) fehler.push('meetingTime')
  return fehler
}

// ── Serie, Laufnummer, Punktnummern (4.) ─────────────────────────────────────

/** Vorschlag beim Anlegen (4.2): höchste Laufnummer der Serie + 1 — verworfene Entwürfe zählen nicht (das filtert der Aufrufer). */
export function besprechungNaechsteLaufnummer(vorhandene: readonly number[]): number {
  return vorhandene.reduce((m, n) => (Number.isInteger(n) && n > m ? n : m), 0) + 1
}

/** Die nächste Nummer eines neuen Punkts: `{Laufnummer}.{laufend}` — übernommene Nummern zählen nicht mit. */
export function besprechungNaechstePunktnummer(laufnummer: number, punkte: readonly Pick<BesprechungPunkt, 'number'>[]): string {
  const vorsatz = `${laufnummer}.`
  const hoechste = punkte.reduce((m, p) => {
    if (!p.number?.startsWith(vorsatz)) return m
    const n = Number(p.number.slice(vorsatz.length))
    return Number.isInteger(n) && n > m ? n : m
  }, 0)
  return `${vorsatz}${hoechste + 1}`
}

/** Titel eines Protokolls: „Baubesprechung Nr. 7" — was die Teilnehmer lesen (BS-1). */
export function besprechungTitel(art: string, laufnummer: number): string {
  return `${art.trim() || 'Besprechung'} Nr. ${laufnummer}`
}

// ── Fortschreibung (4.3) ─────────────────────────────────────────────────────

/** Die Quelle der Fortschreibung: die letzte freigegebene Besprechung der Serie. */
export interface BesprechungFortschreibungQuelle {
  documentId: string
  number: string | null
  laufnummer: number
  documentDate: string
  tagesordnung: BesprechungTop[]
  teilnehmer: BesprechungTeilnehmer[]
  punkte: (BesprechungPunkt & { originId: string })[]
}

/** Was übernommen wird — Teilnehmer ohne Anwesenheit, offene Aufgaben samt ihren Tagesordnungspunkten. */
export interface BesprechungUebernahme {
  tagesordnung: BesprechungTop[]
  teilnehmer: BesprechungTeilnehmer[]
  punkte: BesprechungPunkt[]
}

/**
 * **Fortschreibung** (4.3, BS-3): Teilnehmer samt Verteiler mit leerer Anwesenheit; offene Aufgaben mit
 * ihrer ursprünglichen Nummer, Herkunft („aus Nr. 5" — die erste Erfassung, nicht die letzte Besprechung)
 * und `originId`; die Tagesordnungspunkte, unter denen sie standen. Info und Beschluss wandern nicht.
 * `neueId` liefert Kennungen für die neuen Zeilen (Gerät: `crypto.randomUUID`).
 */
export function besprechungFortschreibung(quelle: BesprechungFortschreibungQuelle, neueId: () => string): BesprechungUebernahme {
  const offen = besprechungPunkteSortiert(quelle.punkte, quelle.tagesordnung).filter((p) => p.kind === 'TASK' && p.state === 'OPEN')
  const tops = new Set(offen.map((p) => p.agendaKey).filter((k): k is string => !!k))
  return {
    tagesordnung: quelle.tagesordnung.filter((x) => tops.has(x.key)).map((x) => ({ ...x })),
    teilnehmer: besprechungTeilnehmerSortiert(quelle.teilnehmer).map((p, i) => ({
      clientId: neueId(),
      name: p.name,
      company: p.company ?? null,
      function: p.function ?? null,
      attendance: null,
      distribution: p.distribution,
      sortOrder: i,
      email: p.email ?? null,
      userId: p.userId ?? null,
      contactId: p.contactId ?? null,
    })),
    punkte: offen.map((p, i) => ({
      clientId: neueId(),
      originId: p.originId,
      number: p.number,
      agendaKey: p.agendaKey,
      kind: 'TASK' as const,
      text: p.text,
      assigneeName: p.assigneeName ?? null,
      assigneeCompany: p.assigneeCompany ?? null,
      assigneeUserId: p.assigneeUserId ?? null,
      dueDate: p.dueDate ?? null,
      dueText: p.dueText ?? null,
      state: 'OPEN' as const,
      carriedFrom: p.carriedFrom || `aus Nr. ${quelle.laufnummer}`,
      sortOrder: i,
    })),
  }
}

/** `GET /site-docs/meeting/config` — was Anlegedialog und Maske über die Art wissen (Besprechungsarten, Frist). */
export interface BesprechungKonfig {
  arten: string[]
  einspruchTage: number
  /** Name des Betriebs — Firma der Mitarbeiter als Teilnehmer. */
  firma: string
}

/** `GET /site-docs/meeting/carry-over` — Vorschlag der Laufnummer, Übernahme und Hinweise (4.3). */
export interface BesprechungFortschreibungAntwort {
  /** Vorschlag der Laufnummer für ein neues Protokoll dieser Serie. */
  laufnummer: number
  /** Laufnummern derselben Serie, die schon vergeben sind (für den Hinweis „doppelt"). */
  vergeben: number[]
  /** Die letzte freigegebene Besprechung der Serie — `null`, wenn es keine gibt. */
  quelle: { documentId: string; number: string | null; laufnummer: number; documentDate: string } | null
  uebernahme: BesprechungUebernahme
  hinweise: {
    /** Berichtigung zur letzten Besprechung (BS-11). */
    berichtigung: { documentId: string; number: string | null } | null
    /** Mit Projektverwaltung: Aufgaben, die im Projekt schon erledigt sind (BS-12) — je `originId`. */
    erledigt: { originId: string; number: string; doneAt: string }[]
  }
}

// ── Offene Punkte (8.) ───────────────────────────────────────────────────────

/** Eine Zeile der Liste „Offene Punkte" — je Serie die offenen Aufgaben des letzten freigegebenen Protokolls. */
export interface BesprechungOffenerPunkt {
  /** Kennung des Punkts im letzten Protokoll. */
  id: string
  originId: string
  documentId: string
  documentNumber: string | null
  projectLabel: string
  projectId: string | null
  projectRefId: string | null
  /** Serie: Besprechungsart. */
  art: string
  laufnummer: number
  number: string
  text: string
  assigneeName: string | null
  assigneeCompany: string | null
  /** Ein Mitarbeiter des Betriebs (Zuordnung). */
  internal: boolean
  dueDate: string | null
  dueText: string | null
  overdue: boolean
  /** Datum der Besprechung, in der der Punkt zuerst erfasst wurde. */
  since: string
  /** Datum der letzten Besprechung, in der er stand. */
  lastDiscussed: string
}

/** Spalten des Exports der offenen Punkte, in der Reihenfolge der Liste. */
export const BESPRECHUNG_OFFENE_PUNKTE_SPALTEN: readonly { key: keyof BesprechungOffenerPunkt; label: string }[] = [
  { key: 'projectLabel', label: 'Projekt' },
  { key: 'art', label: 'Serie' },
  { key: 'number', label: 'Nr.' },
  { key: 'text', label: 'Punkt' },
  { key: 'assigneeName', label: 'Zuständig' },
  { key: 'assigneeCompany', label: 'Firma' },
  { key: 'dueDate', label: 'Termin' },
  { key: 'dueText', label: 'Termin (Text)' },
  { key: 'since', label: 'seit' },
  { key: 'lastDiscussed', label: 'zuletzt besprochen' },
  { key: 'documentNumber', label: 'Protokoll' },
]

// ── Diktat (13.) ─────────────────────────────────────────────────────────────

/** RN-8 — Textgrenze des Diktats der Besprechung: eine Mitschrift ist länger als ein Regietag. */
export const BESPRECHUNG_DIKTAT_MAX = 12_000

/** Ein Eintrag des Diktat-Vorschlags, wie ihn die Art nach Prüfung und Zuordnung liefert. */
export interface BesprechungDiktatPunkt {
  /** Titel des Tagesordnungspunkts oder leer. */
  top: string | null
  /** Ist der Tagesordnungspunkt neu (nicht in der Tagesordnung des Protokolls)? */
  topNeu: boolean
  kind: BesprechungPunktart
  text: string
  assigneeName: string | null
  assigneeCompany: string | null
  assigneeUserId: string | null
  /** Zuständig nur als Freitext — nicht eindeutig zugeordnet. */
  assigneeFree: boolean
  dueDate: string | null
  dueText: string | null
}

/**
 * Übernahme eines Diktat-Vorschlags (13.) — dieselbe Regel in PWA und ERP: Tagesordnungspunkte nach Titel
 * (unbekannt → neu), Zuständig im Freitext gegen die Teilnehmer des Protokolls (nur eindeutig, nach Name),
 * neue Nummern `{Laufnummer}.{n}`, Aufgaben beginnen „offen". Übernommene Punkte des Protokolls ändert der
 * Vorschlag nie — er ergänzt (AP-46 21.1).
 */
export function besprechungAusDiktat(
  inhalt: Pick<BesprechungInhalt, 'laufnummer' | 'tagesordnung' | 'teilnehmer' | 'punkte'>,
  gewaehlt: readonly BesprechungDiktatPunkt[],
  neueId: () => string,
): { tagesordnung: BesprechungTop[]; neueTops: BesprechungTop[]; punkte: BesprechungPunkt[] } {
  const tops = [...inhalt.tagesordnung]
  const neueTops: BesprechungTop[] = []
  const topFuer = (titel: string | null): string | null => {
    const t = titel?.trim()
    if (!t) return null
    const da = tops.find((x) => x.titel.trim().toLocaleLowerCase('de') === t.toLocaleLowerCase('de'))
    if (da) return da.key
    const neu = { key: `top-${neueId()}`, titel: t }
    tops.push(neu)
    neueTops.push(neu)
    return neu.key
  }
  const alle = [...inhalt.punkte]
  let sortOrder = alle.reduce((m, p) => Math.max(m, p.sortOrder), -1) + 1
  const punkte: BesprechungPunkt[] = []
  for (const d of gewaehlt) {
    const aufgabe = d.kind === 'TASK'
    let name = aufgabe ? d.assigneeName : null
    let firma = aufgabe ? d.assigneeCompany : null
    let userId = aufgabe ? d.assigneeUserId : null
    if (aufgabe && d.assigneeFree && name) {
      const g = name.trim().toLocaleLowerCase('de')
      const treffer = inhalt.teilnehmer.filter((p) => p.name.trim().toLocaleLowerCase('de') === g || p.name.trim().toLocaleLowerCase('de').endsWith(` ${g}`))
      if (treffer.length === 1) {
        name = treffer[0].name
        firma = treffer[0].company ?? null
        userId = treffer[0].userId ?? null
      }
    }
    const p: BesprechungPunkt = {
      clientId: neueId(),
      number: besprechungNaechstePunktnummer(inhalt.laufnummer, alle),
      agendaKey: topFuer(d.top),
      kind: d.kind,
      text: d.text,
      assigneeName: name,
      assigneeCompany: firma,
      assigneeUserId: userId,
      dueDate: aufgabe ? d.dueDate : null,
      dueText: aufgabe ? d.dueText : null,
      state: aufgabe ? 'OPEN' : null,
      sortOrder: sortOrder++,
    }
    alle.push(p)
    punkte.push(p)
  }
  return { tagesordnung: tops, neueTops, punkte }
}
